<template>
  <!-- Absolutely positioned INSIDE the video wrapper, so it stays over the video
       in both normal AND fullscreen (the wrapper itself is the fullscreen element).
       pointer-events:none so it never blocks the video controls. -->
  <transition name="vwm-fade">
    <div
      v-if="displayText && visible"
      class="video-watermark"
      :style="posStyle"
      aria-hidden="true"
    >طالب: {{ displayText }}</div>
  </transition>
</template>

<script>
// Safe positions around the video. Values are % from the top/side. They avoid:
//  - the bottom control bar (nothing below ~66%),
//  - the dead-centre play/pause button.
const SAFE_POSITIONS = [
  { top: '7%',  side: 'left',  offset: '5%'  }, // top-left
  { top: '7%',  side: 'right', offset: '5%'  }, // top-right
  { top: '40%', side: 'left',  offset: '4%'  }, // centre-left
  { top: '40%', side: 'right', offset: '4%'  }, // centre-right
  { top: '62%', side: 'left',  offset: '6%'  }, // lower-left (above controls)
  { top: '62%', side: 'right', offset: '6%'  }, // lower-right (above controls)
];

export default {
  name: 'VideoWatermark',
  props: {
    // The authenticated student's existing identifier. Null/empty => render nothing
    // (never show a fake id, and it disappears the moment the student is gone).
    studentId: { type: [String, Number], default: null },
  },
  data() {
    return {
      visible: true,
      posIndex: 0,
      timer: null,
      // Timings (ms): VISIBLE → HIDE → (move) → SHOW, ~20s period.
      showMs: 5000,
      hideMs: 15000,
    };
  },
  computed: {
    displayText() {
      const v = this.studentId;
      return v === 0 || v ? String(v) : '';
    },
    posStyle() {
      const p = SAFE_POSITIONS[this.posIndex] || SAFE_POSITIONS[0];
      return {
        top: p.top,
        [p.side]: p.offset,
      };
    },
  },
  watch: {
    // Restart the cycle cleanly if the student identity changes (e.g. re-login).
    studentId() {
      this.restart();
    },
  },
  mounted() {
    // Start at a random safe position so two students' recordings don't align.
    this.posIndex = Math.floor(Math.random() * SAFE_POSITIONS.length);
    this.startCycle();
  },
  beforeUnmount() {
    this.stopCycle();
  },
  methods: {
    startCycle() {
      this.stopCycle();
      if (!this.displayText) return;
      this.visible = true;
      // Chained timers (not setInterval) so phases never overlap and the cycle is
      // independent of the video controls being opened/closed.
      this.timer = setTimeout(this.hidePhase, this.showMs);
    },
    hidePhase() {
      this.visible = false;                 // fade out
      this.timer = setTimeout(() => {
        this.posIndex = this.nextPosition(); // move WHILE hidden
        this.visible = true;                 // fade in at the new spot
        this.timer = setTimeout(this.hidePhase, this.showMs);
      }, this.hideMs);
    },
    nextPosition() {
      // Randomised but never the same spot twice in a row.
      if (SAFE_POSITIONS.length < 2) return 0;
      let n = this.posIndex;
      while (n === this.posIndex) {
        n = Math.floor(Math.random() * SAFE_POSITIONS.length);
      }
      return n;
    },
    stopCycle() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    restart() {
      this.stopCycle();
      this.startCycle();
    },
  },
};
</script>

<style scoped>
.video-watermark {
  position: absolute;
  z-index: 40;
  pointer-events: none;
  padding: 3px 9px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.32);
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.2;
  white-space: nowrap;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.65);
  user-select: none;
}

/* Slightly larger + more legible when the wrapper is fullscreen. */
:fullscreen .video-watermark,
:-webkit-full-screen .video-watermark {
  font-size: 16px;
  padding: 4px 12px;
}

.vwm-fade-enter-active,
.vwm-fade-leave-active {
  transition: opacity 0.9s ease;
}
.vwm-fade-enter-from,
.vwm-fade-leave-to {
  opacity: 0;
}
</style>
